/**
 * The onboarding "add another way to sign in" step (Q274). Reuses the
 * shipped `LinkProviderPanel` and `EmailChangeForm` from
 * `../signInMethods/` verbatim; this file owns no link or email-confirm
 * call of its own. `Continue` and `Skip` both advance identically and this
 * step never hard-gates: see plan.md WI-5.
 */
import { useIdentityMethodsGet } from "@sportsmet/auth-sdk/private";
import { useOauthProviderConfigGet } from "@sportsmet/auth-sdk/public";
import { useProfileUpdate } from "@sportsmet/profile-sdk/private";
import { Button } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { EmailChangeForm } from "../signInMethods/EmailChangeForm";
import { LinkProviderPanel } from "../signInMethods/LinkProviderPanel";
import {
  isMethodLinked,
  methodRowLabel,
} from "../signInMethods/signInMethodsScreenLogic";
import {
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  ONBOARDING_SKIP_LABEL,
  SIGN_IN_METHODS_ADD_EMAIL_LABEL,
  SIGN_IN_METHODS_ADDED_LABEL,
  SIGN_IN_METHODS_EMAIL_HELP,
  SIGN_IN_METHODS_HEADLINE,
  SIGN_IN_METHODS_SUB,
  signInMethodConflictMessage,
} from "./onboardingCopy";
import { OnboardingStepFrame } from "./OnboardingStepFrame";
import {
  resolveProviderIds,
  type typeSignInMethodOffer,
} from "./signInMethodOffers";

export type typeSignInMethodsStepProps = {
  stepIndex: number;
  stepTotal: number;
  /** Frozen at container entry (WI-4). Never empty: the container does not render this step for an empty list. */
  offers: readonly typeSignInMethodOffer[];
  /** URL state. undefined means the row list is showing. */
  emailStep: "add-email" | "code" | undefined;
  requestId: string | undefined;
  onOpenEmail: () => void;
  onEmailRequested: (args: { requestId: string }) => void;
  onEmailConfirmed: () => void;
  onEmailCancel: () => void;
  /** Continue and Skip both call this, after /profile/update succeeds. */
  onAdvanced: (input: { reachedServerStep: string | null }) => void;
};

export const SignInMethodsStep = ({
  stepIndex,
  stepTotal,
  offers,
  emailStep,
  requestId,
  onOpenEmail,
  onEmailRequested,
  onEmailConfirmed,
  onEmailCancel,
  onAdvanced,
}: typeSignInMethodsStepProps): ReactNode => {
  const methods = useIdentityMethodsGet({});
  const providerConfig = useOauthProviderConfigGet({});
  const update = useProfileUpdate();
  const isSaving = useRef(false);
  const [conflictProvider, setConflictProvider] = useState<
    "apple" | "google" | null
  >(null);

  const liveMethods = methods.data?.data ?? null;
  const { appleWebServicesId, googleWebClientId } = resolveProviderIds(
    providerConfig.data?.data,
  );

  useEffect(() => {
    if (
      conflictProvider !== null &&
      isMethodLinked(liveMethods, conflictProvider)
    ) {
      setConflictProvider(null);
    }
  }, [liveMethods, conflictProvider]);

  async function handleAdvance(): Promise<void> {
    if (isSaving.current) return;
    isSaving.current = true;

    let result;
    try {
      result = await update.mutateAsync({ onboardingStep: "photo" });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(result)) {
      isSaving.current = false;
      return;
    }

    onAdvanced({
      reachedServerStep: result.data?.profile.onboardingStep ?? "photo",
    });
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={SIGN_IN_METHODS_HEADLINE}
      sub={SIGN_IN_METHODS_SUB}
    >
      {emailStep !== undefined ? (
        <EmailChangeForm
          step={emailStep}
          requestId={requestId}
          onRequested={onEmailRequested}
          onConfirmed={onEmailConfirmed}
          onCancel={onEmailCancel}
        />
      ) : (
        <>
          {offers.map((method) => {
            const linked = isMethodLinked(liveMethods, method);
            return (
              <div key={method}>
                <div
                  className="flex items-center justify-between gap-3"
                  data-testid={`onboarding-sign-in-method-row-${method}`}
                >
                  <span className="text-body text-ink">
                    {methodRowLabel(method)}
                  </span>
                  {linked ? (
                    <span
                      className="flex items-center gap-1 text-body text-ink"
                      data-testid={`onboarding-sign-in-method-added-${method}`}
                    >
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m5 13 4 4L19 7" />
                      </svg>
                      {SIGN_IN_METHODS_ADDED_LABEL}
                    </span>
                  ) : method === "apple" ? (
                    <LinkProviderPanel
                      provider="apple"
                      webServicesId={appleWebServicesId}
                      webClientId={null}
                      onConflict={() => {
                        setConflictProvider("apple");
                      }}
                    />
                  ) : method === "google" ? (
                    <LinkProviderPanel
                      provider="google"
                      webServicesId={null}
                      webClientId={googleWebClientId}
                      onConflict={() => {
                        setConflictProvider("google");
                      }}
                    />
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="onboarding-sign-in-method-add-email"
                      onClick={onOpenEmail}
                    >
                      {SIGN_IN_METHODS_ADD_EMAIL_LABEL}
                    </Button>
                  )}
                </div>
                {!linked &&
                (method === "apple" || method === "google") &&
                conflictProvider === method ? (
                  <p
                    role="alert"
                    className="text-caption text-error"
                    data-testid={`onboarding-sign-in-method-conflict-${method}`}
                  >
                    {signInMethodConflictMessage(method)}
                  </p>
                ) : null}
              </div>
            );
          })}
          {offers.includes("email") ? (
            <p className="text-caption text-ink-secondary">
              {SIGN_IN_METHODS_EMAIL_HELP}
            </p>
          ) : null}
          <div className="sm-screen-bottom flex flex-col gap-2 pt-2">
            <Button
              fullWidth
              loading={update.isPending}
              loadingLabel={CONTINUE_PENDING_LABEL}
              disabled={false}
              data-testid="onboarding-sign-in-methods-continue"
              onClick={() => {
                void handleAdvance();
              }}
            >
              {CONTINUE_LABEL}
            </Button>
            <Button
              variant="link"
              fullWidth
              data-testid="onboarding-sign-in-methods-skip"
              onClick={() => {
                void handleAdvance();
              }}
            >
              {ONBOARDING_SKIP_LABEL}
            </Button>
          </div>
        </>
      )}
    </OnboardingStepFrame>
  );
};
